import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Box, Typography } from '@mui/material';

// helper: given an ISO date string, return a JS Date of the next business day
function getNextBusinessDay(isoDate) {
    // parse as local date at midnight
    const [year, month, day] = isoDate.split('-').map(Number);
    const d = new Date(year, month - 1, day);
  
    // move to tomorrow
    d.setDate(d.getDate() + 1);
  
    // if that falls on Sat(6) or Sun(0), keep advancing
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

// helper to format a number as USD currency
function formatUSD(num) {
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

export default function DailyEntries({ uploadId, refreshKey, uploadDate }) {
  const [entries,   setEntries]   = useState([]);
  const [coaters,   setCoaters]   = useState([]);
  const [saving,    setSaving]    = useState(false);

  // Build the header date as “next business day” after uploadDate
  const formattedDate = uploadDate
    ? (() => {
        const coatDay = getNextBusinessDay(uploadDate);
        return coatDay
          .toLocaleDateString('en-US', {
            month: 'short',
            day:   'numeric',
            year:  'numeric'
          })
          .replace(/^([A-Za-z]{3}) /, '$1. ');
      })()
    : '';

  // Fetch entries + coaters on mount or when uploadId or refreshKey changes
  useEffect(() => {
    axios
      .get(`/api/entries/${uploadId}/`)
      .then(res => {
        setEntries(res.data.entries);
        setCoaters(res.data.coaters);
      })
      .catch(err => {
        console.error("Failed to load daily entries:", err);
        setEntries([]);
        setCoaters([]);
      });
  }, [uploadId, refreshKey]);

  // Update a single field in an entry (by array index)
  const updateField = (idx, field, value) => {
    const copy = [...entries];
    copy[idx] = { ...copy[idx], [field]: value };
    setEntries(copy);
  };

  // Save all changes back to the server
  const saveAll = () => {
    setSaving(true);
    axios
      .put(`/api/entries/${uploadId}/update/`, { entries })
      .then(res => {
        alert(`Updated ${res.data.updated.length} entries`);
      })
      .catch(err => {
        console.error("Failed to save entries:", err);
        alert("Error saving. Check console.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      {/* Print header + button (hidden in print via .no-print) */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        className="no-print"
      >
        <Typography variant="h5">
          {formattedDate} Coating List
        </Typography>
        <Button variant="outlined" onClick={() => window.print()}>
          Print Coating List
        </Button>
      </Box>

      {/* Metrics bar */}
      <div
        style={{
          marginBottom:  '10px',
          padding:       '8px',
          backgroundColor: '#f0f0f0',
          borderRadius:  '4px',
          fontWeight:    'bold'
        }}
      >
        Estimated Coating Value:{' '}
        {formatUSD(
          entries.reduce((sum, e) => sum + (e.remaining_balance || 0), 0)
        )}
      </div>

      {/* Save all button */}
      <button onClick={saveAll} disabled={saving} style={{ marginBottom: 10 }}>
        {saving ? 'Saving…' : 'Save All'}
      </button>

      {/* Printable area */}
      <Box id="print-area">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: 4 }}>Ticket</th>
              <th className="col-name" style={{ padding: 4 }}>Job Title</th>
              <th className="col-reason" style={{ padding: 4 }}>Colors</th>
              <th className="col-order" style={{ padding: 4 }}>Order</th>
              <th style={{ padding: 4 }}>Coater</th>
              <th style={{ padding: 4 }}>Total Value</th>
              <th style={{ padding: 4 }}>Remaining</th>
              <th style={{ padding: 4 }}>Attempted Value</th>
              <th className="col-pass_fail" style={{ padding: 4 }}>Pass/Fail</th>
              <th className="col-notes" style={{ padding: 4 }}>Notes</th>
              <th style={{ padding: 4 }}>Remove</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={e.entry_id}>
                <td style={{ border: '1px solid #ddd', padding: 4 }}>
                  {e.ticket_number}
                </td>
                <td className="col-name" style={{ border: '1px solid #ddd', padding: 4 }}>
                  {e.name}
                </td>
                <td className="col-reason" style={{ border: '1px solid #ddd', padding: 4 }}>
                  {e.colors}
                </td>
                {/* blank order cell for handwriting */}
                <td className="col-order" style={{ border: '1px solid #ddd', padding: 4 }} />
                <td style={{ border: '1px solid #ddd', padding: 4 }}>
                  <select
                    value={e.coater_id || ''}
                    onChange={a =>
                      updateField(i, 'coater_id', Number(a.target.value))
                    }
                    style={{ width: '100%' }}
                  >
                    <option value="" disabled>
                      — pick —
                    </option>
                    {coaters.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td
                  style={{
                    border:      '1px solid #ddd',
                    padding:     4,
                    textAlign:   'right'
                  }}
                >
                  {formatUSD(e.total_value)}
                </td>
                <td
                  style={{
                    border:      '1px solid #ddd',
                    padding:     4,
                    textAlign:   'right'
                  }}
                >
                  {formatUSD(e.remaining_balance)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: 4 }}>
                  <input
                    type="number"
                    value={e.attempted_value}
                    onChange={a =>
                      updateField(i, 'attempted_value', Number(a.target.value))
                    }
                    style={{ width: '100%' }}
                  />
                </td>
                <td className="col-pass_fail"
                  style={{
                    border:      '1px solid #ddd',
                    padding:     4,
                    textAlign:   'center'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={e.pass_fail}
                    onChange={a => updateField(i, 'pass_fail', a.target.checked)}
                  />
                </td>
                <td className="col-notes" style={{ border: '1px solid #ddd', padding: 4 }}>
                  <textarea
                    value={e.notes}
                    onChange={a => updateField(i, 'notes', a.target.value)}
                    rows={1}
                    style={{ width: '100%' }}
                  />
                </td>
                <td
                  style={{
                    border:      '1px solid #ddd',
                    padding:     4,
                    textAlign:   'center'
                  }}
                >
                  <button
                    onClick={() => {
                      axios
                        .post(
                          `/api/uploads/${uploadId}/jobs/remove_selected/`,
                          { job_ids: [e.job_pk] }
                        )
                        .then(() =>
                          setEntries(
                            entries.filter(x => x.entry_id !== e.entry_id)
                          )
                        )
                        .catch(err => {
                          console.error('Remove failed:', err);
                          alert('Error removing job. See console.');
                        });
                    }}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </div>
  );
}
